/**
 * HolderRewardsSummary - Shows a holder's points, tier breakdown, and reward address status
 */

import React from 'react';
import { Badge } from '../../ui/components/Badge';

interface HolderRewardsSummaryProps {
  myRewards: Array<{
    points: number;
    payout_amount: number;
    reward_address: string | null;
    is_unregistered: boolean;
    points_breakdown: Record<string, { name: string; points: number; nft_count: number }> | null;
  }>;
  rewardCurrencyLabel?: string;
}

export const HolderRewardsSummary: React.FC<HolderRewardsSummaryProps> = ({
  myRewards,
  rewardCurrencyLabel = 'COREUM',
}) => {
  if (myRewards.length === 0) return null;

  const current = myRewards[0];

  return (
    <div className="flex flex-col gap-3 rounded border border-solid border-neutral-border p-4">
      <div className="flex items-center justify-between">
        <span className="text-body-bold font-body-bold text-default-font">Your Rewards</span>
        {current.is_unregistered ? (
          <Badge variant="warning">Unregistered</Badge>
        ) : current.reward_address ? (
          <Badge variant="success">Registered</Badge>
        ) : (
          <Badge variant="neutral">No reward address</Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-caption font-caption text-subtext-color block">Points</span>
          <span className="text-heading-3 font-heading-3 text-default-font">
            {Number(current.points).toLocaleString()}
          </span>
        </div>
        <div>
          <span className="text-caption font-caption text-subtext-color block">Payout</span>
          <span className="text-heading-3 font-heading-3 text-default-font">
            {Number(current.payout_amount).toLocaleString(undefined, { maximumFractionDigits: 6 })} {rewardCurrencyLabel}
          </span>
        </div>
      </div>

      {current.reward_address && (
        <div className="text-caption font-caption text-subtext-color">
          Reward address: <span className="font-mono">{current.reward_address.slice(0, 12)}...{current.reward_address.slice(-6)}</span>
        </div>
      )}

      {current.points_breakdown && Object.keys(current.points_breakdown).length > 0 && (
        <div className="flex flex-col gap-1 mt-1">
          <span className="text-caption-bold font-caption-bold text-subtext-color">Breakdown</span>
          {Object.entries(current.points_breakdown).map(([colId, info]) => (
            <div key={colId} className="flex justify-between text-caption font-caption text-default-font">
              <span>{info.name || `${info.nft_count} NFTs`}</span>
              <span>{Number(info.points).toLocaleString()} pts</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
